import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { FALLBACK_GALLERIES } from '../data';

export default function GallerySection({ slug }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch('https://api.dev.auxo.dk/public/content/galleri')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente gallerier');
        return res.json();
      })
      .then((data) => {
        const found = data.find((g) => g.tech_slug === slug);
        if (found) {
          setGallery(found);
        } else {
          // Prøv fallback
          setGallery(FALLBACK_GALLERIES[slug] || null);
        }
      })
      .catch((err) => {
        console.warn('CMS galleri fejlede, bruger fallback:', err);
        setGallery(FALLBACK_GALLERIES[slug] || null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-yellow border-t-transparent" />
      </div>
    );
  }

  if (!gallery || !gallery.images || gallery.images.length === 0) {
    return null;
  }

  const handlePrev = (e) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => (prev === 0 ? gallery.images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setActiveImageIndex((prev) => (prev === gallery.images.length - 1 ? 0 : prev + 1));
  };

  // Cloudflare on-the-fly billedoptimering
  const getOptimizedUrl = (url, width = 600) => {
    if (url.startsWith('https://assets.auxodev.dk/')) {
      return url.replace(
        'https://assets.auxodev.dk/',
        `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
      );
    }
    return url;
  };

  return (
    <section className="mt-12 border-t border-brand-grey/50 pt-12">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-white uppercase tracking-wide border-l-4 border-brand-yellow pl-3">
          Galleri: {gallery.title}
        </h3>
        {gallery.description && (
          <p className="mt-2 text-sm text-gray-400 max-w-2xl">{gallery.description}</p>
        )}
      </div>

      {/* Billed-grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        {gallery.images.map((img, idx) => (
          <div
            key={idx}
            onClick={() => setActiveImageIndex(idx)}
            className="group relative aspect-4/3 overflow-hidden rounded-lg border border-brand-grey bg-brand-dark cursor-pointer transition-all duration-300 hover:border-brand-yellow/50"
          >
            <img
              src={getOptimizedUrl(img, 600)}
              alt={`${gallery.title} hos Vesthimmerlands Materieludlejning - Galleribillede ${idx + 1}`}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="rounded-full bg-brand-yellow p-3 text-white shadow-lg">
                <ZoomIn className="h-5 w-5" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox / Fuldskærmsvisning */}
      {activeImageIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-black/95 p-4 md:p-8"
          onClick={() => setActiveImageIndex(null)}
        >
          {/* Luk-knap */}
          <button
            onClick={() => setActiveImageIndex(null)}
            className="absolute top-4 right-4 rounded-full bg-brand-grey/80 p-2.5 text-white hover:bg-brand-yellow hover:text-white transition-all duration-200"
            aria-label="Luk galleri"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Forrige knap */}
          <button
            onClick={handlePrev}
            className="absolute left-4 rounded-full bg-brand-grey/80 p-2.5 text-white hover:bg-brand-yellow hover:text-white transition-all duration-200 md:left-8"
            aria-label="Forrige billede"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          {/* Billede */}
          <div className="relative max-h-[85vh] max-w-[90vw] overflow-hidden rounded-lg border border-brand-grey bg-brand-dark">
            <img
              src={gallery.images[activeImageIndex]} // Fuld opløsning i lightbox
              alt={`${gallery.title} hos Vesthimmerlands Materieludlejning - Fuld visning ${activeImageIndex + 1}`}
              className="max-h-[80vh] max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="bg-brand-dark/90 px-4 py-2.5 text-center text-xs text-gray-400 border-t border-brand-grey">
              Billede {activeImageIndex + 1} af {gallery.images.length}
            </div>
          </div>

          {/* Næste knap */}
          <button
            onClick={handleNext}
            className="absolute right-4 rounded-full bg-brand-grey/80 p-2.5 text-white hover:bg-brand-yellow hover:text-white transition-all duration-200 md:right-8"
            aria-label="Næste billede"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
      )}
    </section>
  );
}
